/**
 * HCE: fade images in as they load.
 *
 * Product and collection imagery previously appeared at full opacity the instant
 * the file landed, so a grid "popped" into place image by image. Every image here
 * lives inside a `.media` box that already reserves its own aspect ratio, so
 * fading the <img> cannot move anything -- it only softens the arrival.
 *
 * The fade class is added from JavaScript and never from Liquid. If this script
 * fails to run, is blocked, or errors, images are simply displayed normally at
 * full opacity; there is no state in which an image can stay invisible.
 *
 * Three further safeguards:
 *   - images already in cache (`complete` with real dimensions) are left alone,
 *     so a repeat visit or a back-navigation shows them instantly with no flash
 *   - a timeout reveals any image whose load/error events never fire
 *   - classes are stripped once the fade finishes, restoring the stylesheet's
 *     own transitions (the card hover zoom, for instance) untouched
 */
(function () {
  'use strict';

  var FADE_CLASS = 'hce-media-fade';
  var SHOWN_CLASS = 'hce-media-shown';
  var MARK = 'hceFade';
  var SELECTOR = '.media > img';
  var SAFETY_MS = 2500;

  // Honour the OS-level motion preference: no fade at all.
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  /** Return the element to its default styling once the fade is over. */
  function settle(img) {
    img.classList.remove(FADE_CLASS, SHOWN_CLASS);
  }

  /** Trigger the fade, then clean up after it. */
  function reveal(img) {
    if (!img.classList.contains(FADE_CLASS)) return;

    // Force a style read so the browser registers opacity:0 before we change it,
    // otherwise the two states collapse into one frame and nothing animates.
    void img.offsetWidth;
    img.classList.add(SHOWN_CLASS);

    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      img.removeEventListener('transitionend', finish);
      settle(img);
    };

    img.addEventListener('transitionend', finish);
    setTimeout(finish, 1000);
  }

  function prepare(img) {
    if (img.dataset[MARK]) return;
    img.dataset[MARK] = '1';

    // Only the first image in a media box. A card's alternate image is a second
    // <img> that base.css deliberately holds at opacity 0 until hover
    // (`.media.media--hover-effect > img + img`). Our fade classes carry higher
    // specificity than that rule, so fading one would force the hover image
    // visible over the front image for the duration of the transition.
    if (img.parentElement && img.parentElement.querySelector('img') !== img) return;

    // Already decoded (cached, or loaded before this script ran): show as-is.
    if (img.complete && img.naturalWidth > 0) return;

    img.classList.add(FADE_CLASS);

    var onSettled = function () {
      img.removeEventListener('load', onSettled);
      img.removeEventListener('error', onSettled);
      reveal(img);
    };

    img.addEventListener('load', onSettled);
    img.addEventListener('error', onSettled);

    // If neither event ever arrives, don't leave the image hidden.
    setTimeout(function () {
      if (img.classList.contains(FADE_CLASS) && !img.classList.contains(SHOWN_CLASS)) {
        onSettled();
      }
    }, SAFETY_MS);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(SELECTOR).forEach(prepare);
  }

  scan(document);

  // Cards and galleries also arrive from AJAX: facet filtering, quick add,
  // the cart drawer, and section rendering in the theme editor.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(SELECTOR)) prepare(node);
          scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
